import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Currency, Subscription, SubscriptionAddon } from '@hero/schema';
import { SUBSCRIPTION_CONFIG } from '@hero/core';
import { cn } from '@hero/web-lib';
import { ZapIcon } from '@hero/web-icons';
import { Button } from '../../../ui/Button/Button';
import { Price } from '../../../app/Price';

export interface AddonsListProps {
  /** Current subscription — drives the live addons + currency context. */
  subscription: Subscription | null;
  /** Add a not-yet-attached addon (recurring). */
  onAddAddon?: (args: { code: string; billing: 'recurring' | 'one_time' }) => void;
  /** Remove a recurring addon (stops at period end). */
  onRemoveAddon?: (args: { code: string }) => void;
  /** Purchase a one-time addon immediately. */
  onPurchaseOneTime?: (args: { code: string }) => void;
}

/**
 * Cross-references `SUBSCRIPTION_CONFIG.addons` with the subscription's
 * live `addons` array to render attached + available addons in a single
 * list with the right pricing label and CTA per state.
 *
 * Styled to match the checkout card visual language (rounded-2xl,
 * primary-tinted glyph, subtle separators between rows).
 */
export function AddonsList({
  subscription,
  onAddAddon,
  onRemoveAddon,
  onPurchaseOneTime,
}: AddonsListProps) {
  const { t } = useTranslation('m-billing');
  const currency: Currency = subscription?.currency ?? 'SAR';
  const interval = subscription?.interval ?? 'month';
  const attached = new Map(subscription?.addons.map(a => [a.code, a]) ?? []);

  const addonCodes = Object.keys(SUBSCRIPTION_CONFIG.addons);

  return (
    <section
      className={cn(
        'flex flex-col gap-5 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm',
      )}>
      <div className="flex items-start gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <ZapIcon className="size-5" />
        </div>
        <div className="flex flex-col gap-0.5">
          <h3 className="text-base font-semibold text-gray-900">
            {t('addon.title')}
          </h3>
          <p className="text-sm text-gray-500">{t('addon.description')}</p>
        </div>
      </div>

      <ul className="flex flex-col gap-2">
        {addonCodes.map(code => {
          const config = SUBSCRIPTION_CONFIG.addons[code];
          if (!config) return null;
          const live = attached.get(code);
          const pricing = config.pricing[currency];
          const recurringPrice = pricing?.[interval];
          const oneTimePrice = pricing?.['oneTime'];
          const intervalLabel = t(`subscription.interval_label.${interval}`, {
            defaultValue: interval,
          });

          return (
            <li
              key={code}
              className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50/50 px-4 py-3">
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-gray-900">
                  {t(`addon.code.${code}`, { defaultValue: code })}
                </span>
                <span className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
                  {recurringPrice !== undefined && (
                    <span className="inline-flex items-center gap-1">
                      <Price amount={recurringPrice} currency={currency} size="xs" />
                      <span>/ {intervalLabel}</span>
                    </span>
                  )}
                  {recurringPrice !== undefined && oneTimePrice !== undefined && (
                    <span aria-hidden>·</span>
                  )}
                  {oneTimePrice !== undefined && (
                    <span className="inline-flex items-center gap-1">
                      <Price amount={oneTimePrice} currency={currency} size="xs" />
                      <span>· {t('addon.billing.one_time')}</span>
                    </span>
                  )}
                </span>
              </div>
              <AddonActions
                code={code}
                live={live}
                config={config}
                onAddAddon={onAddAddon}
                onRemoveAddon={onRemoveAddon}
                onPurchaseOneTime={onPurchaseOneTime}
              />
            </li>
          );
        })}
      </ul>
    </section>
  );
}

interface AddonActionsProps {
  code: string;
  live: SubscriptionAddon | undefined;
  config: (typeof SUBSCRIPTION_CONFIG.addons)[string];
  onAddAddon?: AddonsListProps['onAddAddon'];
  onRemoveAddon?: AddonsListProps['onRemoveAddon'];
  onPurchaseOneTime?: AddonsListProps['onPurchaseOneTime'];
}

function AddonActions({
  code,
  live,
  config,
  onAddAddon,
  onRemoveAddon,
  onPurchaseOneTime,
}: AddonActionsProps) {
  const { t } = useTranslation('m-billing');

  if (live && live.billing === 'recurring') {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onRemoveAddon?.({ code })}
        className="text-red-600 hover:bg-red-50 hover:text-red-700">
        {t('addon.actions.remove')}
      </Button>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {config.type !== 'recurring_only' && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPurchaseOneTime?.({ code })}>
          {t('addon.actions.purchase')}
        </Button>
      )}
      <Button
        variant="default"
        size="sm"
        onClick={() => onAddAddon?.({ code, billing: 'recurring' })}>
        {t('addon.actions.add')}
      </Button>
    </div>
  );
}
